import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ListChecks, Trash2 } from 'lucide-react';
import { Button, IconButton } from '@/components/common/Button';
import UploadStatusBadge from '@/components/upload/UploadStatusBadge';
import UploadIssues from '@/components/upload/UploadIssues';
import { useToast } from '@/components/common/Toast';
import { useUploads } from '@/hooks/useUploads';
import { uploadService } from '@/services/upload.service';
import { uploadsKey } from '@/config/upload';
import { codeMessage, isImportInProgress, uploadErrorMessage } from '@/utils/upload';
import { localeTag } from '@/i18n/format';
import { Upload } from '@/types/upload';

interface UploadListProps {
  workspace: string;
  /** Opens the column check of a waiting upload. */
  onCheck: (upload: Upload) => void;
}

/** A workspace's uploads, newest first: status, what the import made, its problems; withdraw or take off the list. */
const UploadList: React.FC<UploadListProps> = ({ workspace, onCheck }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { data: uploads, isLoading, isError } = useUploads(workspace);
  const [issuesOf, setIssuesOf] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (u: Upload) => uploadService.remove(workspace, u.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: uploadsKey(workspace) }),
    onError: (e) => addToast(uploadErrorMessage(e), 'error'),
  });

  if (isLoading) return <p className="text-xs text-gn-muted">…</p>;
  if (isError) return <p className="text-xs text-gn-danger">{t('uploads.list_failed')}</p>;
  if (!uploads || uploads.length === 0) return <p className="text-xs text-gn-muted italic">{t('uploads.none')}</p>;

  return (
    <ul className="divide-y divide-gn-light border border-gn-light rounded-lg">
      {uploads.map((u) => (
        <li key={u.id} className="px-4 py-3 space-y-1">
          <div className="flex items-center gap-2">
            <span className="flex-1 min-w-0 text-sm text-gn-primary truncate" title={u.fileName}>
              {u.layerName || u.fileName}
              {u.layerName && u.layerName !== u.fileName && <span className="ml-1 text-[11px] text-gn-muted">({u.fileName})</span>}
            </span>
            <UploadStatusBadge status={u.status} />
            {u.status === 'WAITING' && u.canManage && (
              <Button size="sm" variant="secondary" onClick={() => onCheck(u)}>{t('uploads.check_columns')}</Button>
            )}
            {(u.issueCount > 0 || u.status === 'FAILED') && (
              <IconButton size="sm" icon={ListChecks} label={t('uploads.issues')} onClick={() => setIssuesOf(issuesOf === u.id ? null : u.id)} />
            )}
            {u.canManage && (
            <IconButton size="sm" icon={Trash2} disabled={isImportInProgress(u.status) || remove.isPending}
                        label={u.status === 'WAITING' ? t('uploads.withdraw') : t('uploads.remove')}
                        onClick={() => {
                          const ask = u.status === 'WAITING' ? t('uploads.withdraw_confirm') : t('uploads.remove_confirm');
                          if (window.confirm(ask)) remove.mutate(u);
                        }} />
            )}
          </div>
          <div className="text-[11px] text-gn-muted flex flex-wrap gap-x-3">
            {u.uploadedBy && <span>{u.uploadedBy}</span>}
            <span>{new Date(u.createdAt).toLocaleString(localeTag(), { dateStyle: 'medium', timeStyle: 'short' })}</span>
            {u.status === 'DONE' && (
              <span>
                {u.dataOnly ? t('uploads.made_data', { count: u.rowsCreated }) : t('uploads.made_layer', { count: u.rowsCreated })}
              </span>
            )}
            {u.status === 'DONE' && u.rowsWithoutGeometry > 0 && (
              <span className="text-gn-warning">{t('uploads.without_place', { count: u.rowsWithoutGeometry })}</span>
            )}
            {u.issueCount > 0 && <span className="text-gn-warning">{t('uploads.issue_count', { count: u.issueCount })}</span>}
          </div>
          {u.status === 'FAILED' && (u.errorCode || u.error) && (
            <p className="text-xs text-gn-danger flex items-start gap-1">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              {codeMessage(u.errorCode) ?? u.error}
            </p>
          )}
          {issuesOf === u.id && <UploadIssues upload={u} />}
        </li>
      ))}
    </ul>
  );
};

export default UploadList;
